import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router';
import { conciliacaoBancariaAPI } from '../../../../features/conciliacao-bancaria/conciliacaoBancariaAPI';
import { selectStatus, selectMode } from '../../../../features/conciliacao-bancaria/conciliacaoBancariaSelectors';
import {
  changeToCreateMode,
  changeToConsultMode,
  changeToUpdateMode,
} from '../../../../features/conciliacao-bancaria/conciliacaoBancariaSlice';
import QCXRegistrationFormPageTemplate from '../../../../templates/registration-form-page/QCXRegistrationFormPageTemplate';
import { normalizeNumeral } from '../../../../utils/general/general-utils';
import { isConsultMode, isCreateMode, isUpdateMode } from '../../../../utils/store/store-utils';
import QCXConciliacaoBancariaFinanceiroForm from './components/QCXConciliacaoBancariaFinanceiroForm';
import { PAGADOR_TIPO } from '../../../../shared-components/select-pagador-autocomplete/QCXSelectPagadorAutocomplete';
import { setErrorFeedback } from '../../../../features/feedback/feedbackSlice';

export default function ConciliacaoBancariaRegistrationPage({ authInfo = {} }) {
  const { t } = useTranslation();

  const location = useLocation();

  const dispatch = useDispatch();

  const history = useHistory();

  const { id } = useParams();

  const status = useSelector(selectStatus);

  const mode = useSelector(selectMode);

  const [initialValues, setInitialValues] = useState({});
  const [isLoadingConciliacao, setIsLoadingConciliacao] = useState(false);

  const tipoConciliacao = useMemo(
    () => (location?.state?.tipoConciliacao ? location.state.tipoConciliacao : 'CREDITO'),
    [location]
  );

  const isCreate = useMemo(() => isCreateMode(mode), [mode]);

  const isConsult = useMemo(() => isConsultMode(mode), [mode]);

  const isUpdate = useMemo(() => isUpdateMode(mode), [mode]);

  const handleChangeToCreate = useCallback(() => {
    dispatch(changeToCreateMode());
  }, [dispatch]);

  const handleChangeToConsult = useCallback(() => {
    dispatch(changeToConsultMode());
  }, [dispatch]);

  const handleChangeToUpdate = useCallback(() => {
    dispatch(changeToUpdateMode());
  }, [dispatch]);

  const handleCancelUpdate = useCallback(() => handleChangeToConsult(), [handleChangeToConsult]);

  useEffect(() => {
    if (id) {
      dispatch(changeToConsultMode());
      return;
    }

    dispatch(changeToCreateMode());
  }, [id, dispatch]);

  useEffect(() => {
    const fetchConciliacao = async () => {
      if (!id) {
        return;
      }

      try {
        setIsLoadingConciliacao(true);

        const { data } = await conciliacaoBancariaAPI.fetchConciliacaoById(id);

        let pagador = null;

        if (data?.pagadorClienteId) {
          pagador = {
            id: data.pagadorClienteId,
            _pagadorTipo: PAGADOR_TIPO.CLIENTE,
          };
        } else if (data?.pagadorFornecedorId) {
          pagador = {
            id: data.pagadorFornecedorId,
            _pagadorTipo: PAGADOR_TIPO.FORNECEDOR,
          };
        } else if (data?.pagadorEmpresaEstrangeiraId) {
          pagador = {
            id: data.pagadorEmpresaEstrangeiraId,
            _pagadorTipo: PAGADOR_TIPO.EMPRESA_ESTRANGEIRA,
          };
        }

        const values = {
          id: data?.id,
          dataSolicitacao: data?.dataSolicitacao ? new Date(data.dataSolicitacao) : null,
          taxa: data?.taxa,
          tipo: data?.tipo,
          valorAReceber: data?.valor,
          contaBancaria: data?.contaBancaria || null,
          despesaReceita: data?.despesaReceita || null,
          pagador,
        };

        setInitialValues(values);
      } catch (error) {
        dispatch(
          setErrorFeedback({
            message: 'Erro ao consultar conciliação bancária.',
          })
        );

        history.goBack();
      } finally {
        setIsLoadingConciliacao(false);
      }
    };

    fetchConciliacao();
  }, [id, dispatch, history]);

  const breadcrumbs = useMemo(
    () => [
      {
        link: {
          to: '/',
          name: t('com.muralis.qcx.inicio'),
        },
      },
      {
        link: {
          to: t('com.muralis.qcx.url.moduloFinanceiro'),
          name: t('com.muralis.qcx.financeiro.label'),
        },
      },
      {
        link: {
          to: t('com.muralis.qcx.url.moduloFinanceiroConciliacaoBancaria'),
          name: t('com.muralis.qcx.principal'),
        },
      },
      {
        text: {
          name: t('com.muralis.qcx.financeiro.conciliacaoBancaria'),
        },
      },
    ],
    [t]
  );

  const normalize = (data, tipoConciliacaoNorm) => {
    const { pagador, ...rest } = data;

    const result = {
      ...rest,
      valorAReceber: normalizeNumeral(data?.valorAReceber),
      taxa: normalizeNumeral(data?.taxa),
      tipo: tipoConciliacaoNorm,
    };

    if (pagador?._pagadorTipo === PAGADOR_TIPO.CLIENTE) {
      result.pagadorClienteId = pagador.id;
    } else if (pagador?._pagadorTipo === PAGADOR_TIPO.FORNECEDOR) {
      result.pagadorFornecedorId = pagador.id;
    } else if (pagador?._pagadorTipo === PAGADOR_TIPO.EMPRESA_ESTRANGEIRA) {
      result.pagadorEmpresaEstrangeiraId = pagador.id;
    }

    return result;
  };

  const handleSubmit = async (data) => {
    const normalizedData = normalize(data, tipoConciliacao);
    await conciliacaoBancariaAPI.criarConciliacaoBancaria(normalizedData);

    history.goBack();
  };

  return (
    <QCXRegistrationFormPageTemplate
      pageTitle={t('com.muralis.qcx.financeiro.conciliacaoBancaria')}
      showSubtitle={false}
      breadcrumbs={breadcrumbs}
      isCreate={isCreate}
      isUpdate={isUpdate}
      isConsult={isConsult}
      handleChangeToCreate={handleChangeToCreate}
      handleChangeToConsult={handleChangeToConsult}
      handleChangeToUpdate={handleChangeToUpdate}
      handleCancelUpdate={handleCancelUpdate}
      authInfo={authInfo}
    >
      {() => (
        <QCXConciliacaoBancariaFinanceiroForm
          handleSubmit={handleSubmit}
          isCreate={isCreate}
          isConsult={isConsult}
          isUpdate={isUpdate}
          tipoConciliacao={tipoConciliacao}
          initialValues={initialValues}
          isLoading={isLoadingConciliacao}
        />
      )}
    </QCXRegistrationFormPageTemplate>
  );
}